import React, { useEffect, useMemo, useState } from "react";
import { fetchEventImageActivities } from "../../../api/eventImages";
import { requestUploadUrl, createImageRecord } from "./photoApi";
import { stripExif } from "./exifStrip";
import "./PhotoUploadTab.css";

const MAX_BYTES = 10 * 1024 * 1024; // 10MB

const ACCEPTED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

const todayLocalISO = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const isHeic = (file) => {
  if (!file) return false;
  const t = (file.type || "").toLowerCase();
  if (t === "image/heic" || t === "image/heif") return true;
  const name = (file.name || "").toLowerCase();
  return name.endsWith(".heic") || name.endsWith(".heif");
};

const formatBytes = (bytes) => {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const DEFAULT_STATUS = { kind: "idle", message: "" };

const PhotoUploadTab = () => {
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [activitiesError, setActivitiesError] = useState(null);

  const [file, setFile] = useState(null);
  const [strippedBlob, setStrippedBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [activity, setActivity] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [takenDate, setTakenDate] = useState("");
  const [status, setStatus] = useState(DEFAULT_STATUS);

  const isWorking = useMemo(
    () =>
      ["stripping", "requesting", "uploading", "writing"].includes(status.kind),
    [status.kind]
  );

  // Fetch activities on mount
  useEffect(() => {
    let cancelled = false;
    setActivitiesLoading(true);
    fetchEventImageActivities()
      .then((resp) => {
        if (cancelled) return;
        const acts = Array.isArray(resp?.activities) ? resp.activities : [];
        const sorted = [...acts].sort((a, b) =>
          String(a).localeCompare(String(b))
        );
        setActivities(sorted);
        if (sorted.length > 0) {
          setActivity((prev) => prev || sorted[0]);
        }
        setActivitiesLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error("PhotoUploadTab activities error:", err);
        setActivitiesError(err);
        setActivitiesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Manage object URL lifecycle for preview
  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Default displayName when activity changes (only if user hasn't customized it
  // for the current selection — we re-derive whenever a new file is picked)
  const updateDefaultDisplayName = (act) => {
    if (!act) return;
    setDisplayName(`${todayLocalISO()}_${act}`);
  };

  const handleFileChange = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) {
      setFile(null);
      setStrippedBlob(null);
      setTakenDate("");
      setStatus(DEFAULT_STATUS);
      return;
    }

    if (isHeic(f)) {
      setFile(null);
      setStrippedBlob(null);
      setTakenDate("");
      setStatus({
        kind: "error",
        message: "HEIC not supported, please convert first.",
      });
      e.target.value = "";
      return;
    }

    if (!ACCEPTED_TYPES.includes((f.type || "").toLowerCase())) {
      setFile(null);
      setStrippedBlob(null);
      setTakenDate("");
      setStatus({
        kind: "error",
        message: `Unsupported file type "${f.type || "unknown"}". Use JPEG, PNG, or WEBP.`,
      });
      e.target.value = "";
      return;
    }

    setFile(f);
    setStatus(DEFAULT_STATUS);
    if (activity) {
      setDisplayName(`${todayLocalISO()}_${activity}`);
    }

    // Strip EXIF up front so we can read DateTimeOriginal and prefill the
    // Taken Date input. Falls back to today on any failure.
    try {
      const { blob, takenAt } = await stripExif(f);
      setStrippedBlob(blob);
      if (takenAt && typeof takenAt === "string") {
        // takenAt is "YYYY-MM-DDTHH:MM:SS" — keep just the date portion.
        setTakenDate(takenAt.slice(0, 10));
      } else {
        setTakenDate(todayLocalISO());
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("EXIF preprocess failed; using today as Taken Date.", err);
      setStrippedBlob(null);
      setTakenDate(todayLocalISO());
    }
  };

  const handleActivityChange = (e) => {
    const newAct = e.target.value;
    setActivity(newAct);
    if (file) {
      updateDefaultDisplayName(newAct);
    }
  };

  const resetForm = () => {
    setFile(null);
    setStrippedBlob(null);
    setDisplayName("");
    setTakenDate("");
    setStatus(DEFAULT_STATUS);
    // Note: leave activity selection sticky so a user uploading multiple
    // photos for the same activity doesn't have to keep reselecting.
  };

  const handleUpload = async () => {
    if (!file) {
      setStatus({ kind: "error", message: "Select a file first." });
      return;
    }
    if (!activity) {
      setStatus({ kind: "error", message: "Select an activity." });
      return;
    }
    const trimmedName = (displayName || "").trim();
    if (!trimmedName) {
      setStatus({ kind: "error", message: "Display name is required." });
      return;
    }

    try {
      // 1. Strip EXIF (JPEG only). Reuse the blob produced when the file was
      // selected; if for some reason it isn't there (race or earlier failure),
      // strip on the fly so the upload still proceeds.
      let blob = strippedBlob;
      if (!blob) {
        setStatus({ kind: "stripping", message: "Stripping EXIF metadata…" });
        const stripped = await stripExif(file);
        blob = stripped.blob;
      }
      const sizeBytes = blob.size;
      const contentType = file.type || "image/jpeg";

      if (sizeBytes > MAX_BYTES) {
        setStatus({
          kind: "error",
          message: `File too large (${formatBytes(sizeBytes)}). Max is 10 MB.`,
        });
        return;
      }

      // 2. Request presigned URL
      setStatus({ kind: "requesting", message: "Requesting upload URL…" });
      const { uploadUrl, s3Key } = await requestUploadUrl({
        filename: file.name,
        contentType,
        sizeBytes,
        activity,
        displayName: trimmedName,
      });

      // 3. Upload to S3 (no auth header — presigned URL handles it)
      setStatus({ kind: "uploading", message: "Uploading to S3…" });
      const putResp = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": contentType },
        body: blob,
      });
      if (!putResp.ok) {
        throw new Error(`S3 upload failed (${putResp.status})`);
      }

      // 4. Register the record. Send takenAt as ISO local at midnight so the
      // backend can store a sortable timestamp; if the input is empty, omit
      // and let the server default to uploadedAt.
      setStatus({ kind: "writing", message: "Saving photo record…" });
      const takenAtIso = takenDate ? `${takenDate}T00:00:00` : undefined;
      await createImageRecord({
        s3Key,
        displayName: trimmedName,
        activity,
        sizeBytes,
        takenAt: takenAtIso,
      });

      setStatus({
        kind: "success",
        message: `Uploaded "${trimmedName}" to ${activity}.`,
      });
      // Reset file + name + date but keep activity sticky
      setFile(null);
      setStrippedBlob(null);
      setDisplayName("");
      setTakenDate("");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Photo upload error:", err);
      setStatus({
        kind: "error",
        message: err && err.message ? err.message : "Upload failed.",
      });
    }
  };

  const statusClassName = (() => {
    if (status.kind === "error") return "photo-upload-tab__status--error";
    if (status.kind === "success") return "photo-upload-tab__status--success";
    if (status.kind === "idle" || !status.message) return "";
    return "photo-upload-tab__status--info";
  })();

  return (
    <div className="photo-upload-tab">
      <h2>Upload Photo</h2>
      <p className="photo-upload-tab__hint">
        JPEG, PNG, or WEBP. Max 10 MB. EXIF metadata is stripped from JPEGs
        client-side before upload. HEIC is not supported — convert first.
      </p>

      <div className="photo-upload-tab__field">
        <label htmlFor="photo-upload-file">File</label>
        <input
          id="photo-upload-file"
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFileChange}
          disabled={isWorking}
        />
        {file && (
          <div className="photo-upload-tab__file-info">
            {file.name} · {formatBytes(file.size)} · {file.type || "unknown"}
          </div>
        )}
        {previewUrl && (
          <img
            src={previewUrl}
            alt="Selected preview"
            className="photo-upload-tab__preview"
          />
        )}
      </div>

      <div className="photo-upload-tab__field">
        <label htmlFor="photo-upload-activity">Activity</label>
        {activitiesLoading ? (
          <div className="photo-upload-tab__file-info">Loading activities…</div>
        ) : activitiesError ? (
          <div className="photo-upload-tab__status photo-upload-tab__status--error">
            Could not load activities. Try refreshing.
          </div>
        ) : (
          <select
            id="photo-upload-activity"
            value={activity}
            onChange={handleActivityChange}
            disabled={isWorking}
          >
            {activities.length === 0 ? (
              <option value="">(no activities found)</option>
            ) : (
              activities.map((act) => (
                <option key={act} value={act}>
                  {act}
                </option>
              ))
            )}
          </select>
        )}
      </div>

      <div className="photo-upload-tab__field">
        <label htmlFor="photo-upload-display-name">Display Name</label>
        <input
          id="photo-upload-display-name"
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="e.g. 2026-04-30_pickleball"
          disabled={isWorking}
        />
      </div>

      <div className="photo-upload-tab__field">
        <label htmlFor="photo-upload-taken-date">Taken Date</label>
        <input
          id="photo-upload-taken-date"
          type="date"
          value={takenDate}
          onChange={(e) => setTakenDate(e.target.value)}
          disabled={isWorking}
        />
        <div className="photo-upload-tab__file-info">
          Auto-filled from the photo's EXIF DateTimeOriginal when available;
          otherwise defaults to today. Edit to override.
        </div>
      </div>

      {status.message && (
        <div className={`photo-upload-tab__status ${statusClassName}`}>
          {status.message}
        </div>
      )}

      <div className="photo-upload-tab__actions">
        <button
          type="button"
          className="photo-upload-tab__submit"
          onClick={handleUpload}
          disabled={isWorking || !file || !activity}
        >
          {isWorking ? "Working…" : "Upload"}
        </button>
        <button
          type="button"
          className="photo-upload-tab__reset"
          onClick={resetForm}
          disabled={isWorking}
        >
          Reset
        </button>
      </div>
    </div>
  );
};

export default PhotoUploadTab;
